/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/** HTTP 401 — the service rejected the credential the request carried. */
export const HTTP_STATUS_UNAUTHORIZED: number = 401;

/** HTTP 403 — the credential was accepted but does not authorize the call. */
export const HTTP_STATUS_FORBIDDEN: number = 403;

/** Reads `property` off an unknown value without assuming its shape. */
function readProperty(value: unknown, property: string): unknown {
  if (typeof value !== "object" || value === null) {
    return undefined;
  }
  return (value as Record<string, unknown>)[property];
}

/**
 * Extracts the HTTP status from a rejected service call.
 *
 * Service errors reach callers in more than one shape — an Axios-style error
 * carrying `response.status`, or a typed domain-lib error that copied the
 * status onto itself — so both are checked before giving up.
 *
 * @returns The status, or 0 when the error carries none (a transport failure,
 *          an abort, or a client-side validation error).
 */
export function getHttpStatus(error: unknown): number {
  const fromResponse: unknown = readProperty(
    readProperty(error, "response"),
    "status"
  );
  if (typeof fromResponse === "number") {
    return fromResponse;
  }
  const direct: unknown = readProperty(error, "status");
  if (typeof direct === "number") {
    return direct;
  }
  return 0;
}

/** True when the error is an HTTP 401. */
export function isUnauthorizedError(error: unknown): boolean {
  return getHttpStatus(error) === HTTP_STATUS_UNAUTHORIZED;
}

/** True when the error is an HTTP 403. */
export function isForbiddenError(error: unknown): boolean {
  return getHttpStatus(error) === HTTP_STATUS_FORBIDDEN;
}
